// src/pages/ProjectForm.js
import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPencilAlt, faTrashAlt, faSave, faCancel, } from "@fortawesome/free-solid-svg-icons";
import { deleteRotator, getRotatorsByProyectId } from "../services/RotatorServices";
import { deleteStage } from "../services/StageServices";
import { getMeasureUnits } from "../services/CoreServices";
import { getCompanies } from "../services/CompanyServices";
import * as ProjectServices from "../services/ProjectServices";
import { Country, City } from "country-state-city";
import { getCurrencies } from "../services/CurrencyServices";
import { MediaContext } from "../context/MediaContext";
import MediaItems from "../components/MediaItems";
import { TableColor } from "../pages/TableColor";
import styles from "./Table.module.css";
import StatusColorForm from "./StatusColorForm";

import "./ProjectForm.css";
import "./Forms.css";

const STATUS_TRANSLATE = {
  'available': 'disponible',
  'reserved': 'reservado',
  'sold': 'vendido',
  'optioned': 'opcionado',
  'blocked': 'bloqueado'
}

function ProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [cities, setCities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [crmList, setCrmList] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
  const [economicProfiles, setEconomicProfiles] = useState([]);
  const [editingProfileIdx, setEditingProfileIdx] = useState(null);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaSaveMessage, setMediaSaveMessage] = useState("");
  const { mediaItems, setMediaItems } = useContext(MediaContext);
  const [orbitViews, setOrbitViews] = useState([]);
  const [profileDraft, setProfileDraft] = useState({ startPrice: '', endPrice: '', minIncome: '' });
  const [project, setProject] = useState({
    country: "",
    company: "",
    name: "",
    description: "",
    address: "",
    currency: "",
    unit: "",
    orbitViewProject: "",
    orbitViewAmenities: "",
    slug: "",
    interestRate: "",
    backgroundColor: "",
    accentColor: "",
    mediaItems: [],
    prices: [],
    statusColors: {},
    showPrices: true,
    allowQuote: true,
    economicProfiles: [],
    crmList: [],
    createdAt: ""
  });
  const [rotators, setRotators] = useState([]);
  const [section, setSection] = useState('details');
  const [showColorModal, setShowColorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportErrors, setShowImportErrors] = useState(false);
  const [showTableColorModal, setShowTableColorModal] = useState(false);
  const [stages, setStages] = useState([]);
  const tableColorRef = useRef(null);
  const [units, setUnits] = useState([]);
  const [updateCounter, setUpdateCounter] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setCountries(Country.getAllCountries());
        const companies = await getCompanies();
        setCompanies(companies);
        const currencies = await getCurrencies();
        setCurrencies(currencies);
        const units = await getMeasureUnits();
        setUnits(units);

        if (id) {
          const projectData = await ProjectServices.getProjectById(id);
          setProject(projectData);
          setCrmList(projectData.crmList || []);
          setEconomicProfiles(projectData.economicProfiles || []);
          setStages(projectData.stages);
          setMediaItems(projectData.mediaItems);

          if (projectData.country && cities.length === 0) {
            const filteredCities = City.getCitiesOfCountry(projectData.country) || [];
            const uniqueCities = Array.from(
              new Map(filteredCities.map(c => [c.name, c])).values()
            );
            setCities(uniqueCities);
          }

          const rotatorList = await getRotatorsByProyectId(id);
          setRotators(rotatorList);
          setOrbitViews(rotatorList);
        } else {
          const today = new Date().toISOString().split("T")[0];
          setProject(prev => ({ ...prev, createdAt: today }));
          setMediaItems([]);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, countries, setMediaItems, updateCounter, cities.length]);

  const cancelDelete = () => {
    setSection('details');
    setError(null);
    setLoading(false);
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null, confirmationText: '' });
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null });
  };

  const confirmDelete = async () => {
    closeDeleteModal();
    if (deleteInfo.type === "rotator") {
      await handleDeleteRotator(deleteInfo.id);
    } else if (deleteInfo.type === "stage") {
      await handleDeleteStage(deleteInfo.id);
    } else if (deleteInfo.type === "crm") {
      handleRemoveCrm(deleteInfo.id);
    }
  };

  const handleAddCrm = () => {
    setCrmList([
      ...crmList,
      { integration: "", crm: "", config: "" }
    ]);
  };

  const handleAddProfile = () => {
    setEconomicProfiles([
      ...economicProfiles,
      { startPrice: '', endPrice: '', minIncome: '' }
    ]);
    setEditingProfileIdx(economicProfiles.length);
    setProfileDraft({ startPrice: '', endPrice: '', minIncome: '' });
  };

  const handleCopyColors = () => {
    if (tableColorRef.current) {
      const { backgroundColor, accentColor } = tableColorRef.current.getBaseColors();
      setProject(prevProject => ({
        ...prevProject,
        backgroundColor,
        accentColor
      }));
    }
  };

  const handleCountryChange = (countryIsoCode) => {
    setProject({ ...project, country: countryIsoCode, city: "" });

    const filteredCities = City.getCitiesOfCountry(countryIsoCode) || [];
    const uniqueCities = Array.from(
      new Map(filteredCities.map(c => [c.name, c])).values()
    );

    setCities(uniqueCities);
  };

  const handleCrmChange = (index, field, value) => {
    const newList = [...crmList];
    newList[index][field] = value;
    setCrmList(newList);
    setProject({ ...project, crmList: newList });
  };

  const handleDeleteProfile = (idx) => {
    const newProfiles = economicProfiles.filter((_, i) => i !== idx);
    setEconomicProfiles(newProfiles);
    setEditingProfileIdx(null);
  };

  const handleDeleteRotator = async (id) => {
    setError(null);
    setLoading(true);
    try {
      await deleteRotator(id);
      setUpdateCounter(updateCounter + 1);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleDeleteStage = async (id,force=false) => {
    setError(null);
    setLoading(true);
    try {
      await deleteStage(id,force);
      setUpdateCounter(updateCounter + 1);
      setSection('details');
    } catch (error) {
      setError(error.message);
      setDeleteInfo({ type: 'stage', id, confirmationText: '' });
      setSection('deleteStage');
      setLoading(false);
    }
  };

  const handleEditProfile = (idx) => {
    setEditingProfileIdx(idx);
    setProfileDraft({ ...economicProfiles[idx] });
  };

  const handleExport = async () => {
    try {
      setError(null);
      setLoading(true);
      const projectId = id;
      const response = await ProjectServices.exportProject(projectId);
      const data = response?.data ?? response;
      const json = JSON.stringify(data, null, 2);

      // Intentar usar File System Access API
      if (window.showSaveFilePicker) {
        const opts = {
          suggestedName: `${data?.slug ?? 'project'}-${projectId}-export.json`,
          types: [
            {
              description: 'JSON file',
              accept: { 'application/json': ['.json'] }
            }
          ]
        };
        const handle = await window.showSaveFilePicker(opts);
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
      } else {
        // Fallback: crear blob y forzar descarga
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data?.slug ?? 'project'}-${projectId}-export.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setError(null);
      setLoading(true);
      const formData = new FormData();
      formData.append('projectId', id);
      formData.append('file', file, file.name);
      const res = await ProjectServices.importUnits(formData);
      setImportResult(res);
      setShowImportModal(true);
    } catch (err) {
      console.error('Import error', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
      // limpiar input para permitir re-subir mismo archivo si hace falta
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };  

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };  

  const handleNameChange = (e) => {
    const newName = e.target.value;
    const newSlug = newName
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-");
    setProject({ ...project, name: newName, slug: newSlug });
  };

  const handleProfileDraftChange = (e) => {
    const { name, value } = e.target;
    setProfileDraft({ ...profileDraft, [name]: value });
  };

  const handleRemoveCrm = (index) => {
    const newList = crmList.filter((_, i) => i !== index);
    setCrmList(newList);
    setProject({ ...project, crmList: newList });
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      project.mediaItems = mediaItems;
      project.id = id;
      project.economicProfiles = economicProfiles;
      await ProjectServices.saveProject(project);
      setMediaSaveMessage("¡MediaItem guardado exitosamente!");
      setTimeout(() => setMediaSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error saving project:", error);
      setMediaSaveMessage("Error al guardar el MediaItem.");
      setTimeout(() => setMediaSaveMessage(""), 3000);
    }
  };

  const handleSaveProfile = () => {
    const newProfiles = [...economicProfiles];
    newProfiles[editingProfileIdx] = { ...profileDraft };
    setEconomicProfiles(newProfiles);
    setEditingProfileIdx(null);
    setProfileDraft({ startPrice: '', endPrice: '', minIncome: '' });
  };

  const handleStatusColorsChange = (newStatusColors) => {
    setProject(prevProject => ({
      ...prevProject,
      statusColors: newStatusColors
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    project.mediaItems = mediaItems;
    project.economicProfiles = economicProfiles;
    if (id) project.id = id;
    try {
      await ProjectServices.saveProject(project);
      navigate("/projects");
    } catch (error) {
      console.error("Error submitting project:", error);
    }
  };

  const openDeleteModal = (type, id) => {
    setDeleteInfo({ type, id });
    setShowDeleteModal(true);
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (section === 'deleteStage') {
    return (
      <div className="delete-warning-container">
        <h2 style={{ color: 'red' }}>Advertencia</h2>
        <p>
          Si confirmas la eliminación de la etapa, se eliminarán también las bloques y orbit views relacionados.
          Esta operación no puede ser reversada.
        </p>
        <p>Por favor, escribe "eliminar etapa" para confirmar:</p>
        <input
          type="text"
          placeholder="Escribe 'eliminar etapa' aquí"
          onChange={(e) => setDeleteInfo({ ...deleteInfo, confirmationText: e.target.value })}
        />
        <div className="actions-container">
          <button
            className="btn-submit"
            disabled={deleteInfo.confirmationText !== "eliminar etapa"}
            onClick={() => handleDeleteStage(deleteInfo.id, true)}
          >
            Confirmar Eliminación
          </button>
          <button className="btn-cancel" onClick={cancelDelete}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <h2>{id ? "Editar Proyecto" : "Agregar Proyecto"}</h2>
        {error && (
          <div>
            <div className="error-message">Hubo un error: {error}</div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>

          <div className="form-group">
            <label htmlFor="project-createdAt">Fecha de creación</label>
            <input
              type="date"
              id="project-createdAt"
              value={project.createdAt || ""}
              disabled
              readOnly/>
          </div>

          <div className="form-group">
            <label htmlFor="project-country">País</label>
            <select
              value={project.country}
              id="project-country"
              onChange={(e) => handleCountryChange(e.target.value)}
              required
            >
              <option value="">Seleccione un país</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-city">Ciudad</label>
            <select
              value={project.city}
              id="project-city"
              onChange={(e) => setProject({ ...project, city: e.target.value })}
              required
              disabled={!project.country}
            >
              <option value="">Seleccione una ciudad</option>
              {cities.map((city) => (
                <option key={`${city.stateCode}-${city.name}`} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-companyId">Compañía</label>
            <select
              value={project.companyId}
              id="project-companyId"
              onChange={(e) =>
                setProject({ ...project, companyId: e.target.value })
              }
              required
            >
              <option value="">Seleccione una compañía</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-name">Nombre</label>
            <input
              type="text"
              id="project-name"
              value={project.name}
              onChange={handleNameChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="project-description">Descripción</label>
            <input
              type="text"
              id="project-description"
              value={project.description}
              onChange={(e) =>
                setProject({ ...project, description: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="project-address">Dirección</label>
            <input
              type="text"
              id="project-address"
              value={project.address}
              onChange={(e) =>
                setProject({ ...project, address: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="project-currency">Moneda</label>
            <select
              value={project.currency}
              id="project-currency"
              onChange={(e) =>
                setProject({ ...project, currency: e.target.value })
              }
              required
            >
              <option value="">Seleccione una moneda</option>
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-measureUnit">Unidad de Medida</label>
            <select
              value={project.measureUnit}
              id="project-measureUnit"
              onChange={(e) =>
                setProject({ ...project, measureUnit: e.target.value })
              }
              required
            >
              <option value="">Seleccione una unidad</option>
              {units.map((unit) => (
                <option key={unit.code} value={unit.code}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-orbitViewProject">Orbit View (Proyecto)</label>
            <select
              id="project-orbitViewProject"
              value={project.orbitViewProject}
              onChange={(e) => setProject({ ...project, orbitViewProject: e.target.value })}>
              <option value="">Seleccione un órbit</option>
              {orbitViews.map((view) => (
                <option key={view.id} value={view.name}>
                  {view.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-orbitViewAmenities">Orbit View (Amenidades)</label>
            <select
              id="project-orbitViewAmenities"
              value={project.orbitViewAmenities}
              onChange={(e) => setProject({ ...project, orbitViewAmenities: e.target.value })}>
              <option value="">Seleccione un órbit</option>
              {orbitViews.map((view) => (
                <option key={view.id} value={view.name}>
                  {view.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project-slug">Slug (auto-generado)</label>
            <input
              type="text"
              id="project-slug"
              value={project.slug}
              readOnly
            />
          </div>

          <div className="form-group">
            <label htmlFor="project-interestRate">Tasa de interes</label>
            <input
              type="text"
              id="project-interestRate"
              value={project.interestRate}
              onChange={(e) =>
                setProject({ ...project, interestRate: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label style={{ marginBottom: "12px", display: "block" }}>Integraciones CRM</label>
            {crmList.map((crm, idx) => (
              <div key={idx} style={{ marginBottom: "16px", border: "1px solid #eee", padding: "8px" }}>
                <div>
                  <label>Tipo de integración</label>
                  <select
                    value={crm.integration}
                    onChange={e => handleCrmChange(idx, "integration", e.target.value)}
                  >
                    <option value="">Seleccione tipo</option>
                    <option value="base">Unidades y Leads</option>
                    <option value="units">Unidades</option>
                    <option value="leads">Leads</option>
                  </select>
                </div>
                <div>
                  <label>CRM</label>
                  <select
                    value={crm.crm}
                    onChange={e => handleCrmChange(idx, "crm", e.target.value)}
                  >
                    <option value="">Seleccione CRM</option>
                    <option value="monday">Monday</option>
                    <option value="smarthome">Smarthome</option>
                  </select>
                </div>
                <div>
                  <label>Configuración (JSON)</label>
                  <textarea
                    rows="3"
                    value={crm.config}
                    onChange={e => handleCrmChange(idx, "config", e.target.value)}
                    placeholder='{"apiKey":"..."}'
                  />
                </div>
                <button className="btn-cancel" type="button" onClick={() => openDeleteModal("crm", idx)} style={{ marginTop: "8px" }}>
                  Eliminar
                </button>
              </div>
            ))}
            <br />
            <button className="btn-add" type="button" onClick={handleAddCrm}>
              Agregar integración CRM
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="project-showPrices">Mostrar Precios</label>
            <label className="switch" style={{ marginLeft: "10px" }}>
              <input
                type="checkbox"
                id="project-showPrices"
                checked={project.showPrices}
                onChange={() =>
                  setProject({ ...project, showPrices: !project.showPrices })
                }
              />
              <span className="slider round"></span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="project-allowQuote">Permitir Cotizaciones</label>
            <label className="switch" style={{ marginLeft: "10px" }}>
              <input
                type="checkbox"
                id="project-allowQuote"
                checked={project.allowQuote}
                onChange={() =>
                  setProject({ ...project, allowQuote: !project.allowQuote })
                }
              />
              <span className="slider round"></span>
            </label>
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">
              {id ? "Guardar Cambios" : "Agregar Proyecto"}
            </button>
          </div>
        </form>
        <div className="form-footer">
          <Link to="/projects" className="back-link">
            Regresar a Proyectos
          </Link>
        </div>
      </div>

      {project.id && (
        <div className="stages-container">
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-action" onClick={handleExport} disabled={loading || !id}>
              Exportar Proyecto
            </button>

            <button type="button" className="btn-action" onClick={handleImportClick} disabled={loading || !id}>
              Importar Unidades
            </button>

            {/* input file oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.json"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </div>

          <div className="sections-container">
            <MediaItems
              projectSlug={project.slug}
              objectType="project"
              objectSlug="main"
            />
            <button className="btn-submit" onClick={() => handleSave()}>
              Grabar
            </button>
            {mediaSaveMessage && (<div className="media-save-message">{mediaSaveMessage}</div>)}
          </div>

          <div className="sections-container">
            <h2>Tour Virtual</h2>
            <button
              className="btn-add"
              type="button"
              onClick={() => navigate("/tour/upload/" + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          <div className="sections-container">
            <h2>Etapas</h2>
            <button className="btn-add" onClick={() => navigate("/stages/new/" + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>

            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {stages.map((stage) => (
                  <tr key={stage.id}>
                    <td>{stage.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/stages/edit/${id}/${stage.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("stage", stage.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Orbit Views</h2>
            <button className="btn-add" onClick={() => navigate("/rotator/new/" + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "60%" }}>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rotators.map((rotator) => (
                  <tr key={rotator.id}>
                    <td>{rotator.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/rotator/edit/${id}/${rotator.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("rotator", rotator.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Tabla de colores</h2>
            <button className="btn-add" type="button"
              onClick={() => setShowTableColorModal(true)} >
              Ver tabla de colores
            </button>
            {project.backgroundColor && project.accentColor && (
              <div className={styles.colorTableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Background</td>
                      <td>{project.backgroundColor}</td>
                      <td>
                        <div className="color-container">
                          <div className="color-sections" style={{ backgroundColor: project.backgroundColor }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Accent</td>
                      <td>{project.accentColor}</td>
                      <td>
                        <div className="color-container">
                          <div className="color-sections" style={{ backgroundColor: project.accentColor }} />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="sections-container">
            <h2>Colores por estado</h2>
            <button
              className="btn-add"
              type="button"
              onClick={() => setShowColorModal(true)}
            >
              {project.statusColors && Object.keys(project.statusColors).length > 0
                ? "Editar colores"
                : "Agregar colores"}
            </button>

            {project.statusColors && (
              <div className={styles.colorTableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Estado</th>
                      <th>Valor</th>
                      <th>Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(project.statusColors).map(([key, value]) => (
                      <tr key={key}>
                        <td>{STATUS_TRANSLATE[key] || key}</td>
                        <td>{value}</td>
                        <td>
                          <div className="color-container">
                            <div className="color-sections" style={{ backgroundColor: value }} />
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="sections-container">
            <h2>Perfil Económico de Clientes</h2>
            <button className="btn-add" type="button" onClick={handleAddProfile}>
              <FontAwesomeIcon icon={faPlus} /> Agregar perfil
            </button>
            <table className={styles.table} style={{ width: "100%", marginTop: "12px" }}>
              <thead>
                <tr>
                  <th>Precio Inicial</th>
                  <th>Precio Final</th>
                  <th>Ingreso Mínimo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {economicProfiles && economicProfiles.map((profile, idx) =>
                  editingProfileIdx === idx ? (
                    <tr key={idx}>
                      <td>
                        <input
                          type="number"
                          name="startPrice"
                          value={profileDraft.startPrice}
                          onChange={handleProfileDraftChange}
                          step="1"
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="endPrice"
                          value={profileDraft.endPrice}
                          onChange={handleProfileDraftChange}
                          step="1"
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="minIncome"
                          value={profileDraft.minIncome}
                          onChange={handleProfileDraftChange}
                          step="1"
                          min="0"
                        />
                      </td>
                      <td>
                        <button className="btn-submit" type="button" onClick={handleSaveProfile}>
                          <FontAwesomeIcon icon={faSave} />
                        </button>
                        <button className="btn-cancel" type="button" onClick={() => setEditingProfileIdx(null)}>
                          <FontAwesomeIcon icon={faCancel} />
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={idx}>
                      <td>{profile.startPrice !== undefined && profile.startPrice !== '' ? Number(profile.startPrice).toLocaleString() : ''}</td>
                      <td>{profile.endPrice !== undefined && profile.endPrice !== '' ? Number(profile.endPrice).toLocaleString() : ''}</td>
                      <td>{profile.minIncome !== undefined && profile.minIncome !== '' ? Number(profile.minIncome).toLocaleString() : ''}</td>
                      <td>
                        <button className="btn-edit" type="button" onClick={() => handleEditProfile(idx)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" type="button" onClick={() => handleDeleteProfile(idx)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>          

          {/* Modal para TableColor */}
          {showTableColorModal && (
            <div className="modal-overlay" onClick={() => setShowTableColorModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button
                  className="close-button"
                  onClick={() => setShowTableColorModal(false)}
                  aria-label="Cerrar modal"
                >
                  &times;
                </button>
                <TableColor
                  ref={tableColorRef}
                  initialBackgroundColor={project.backgroundColor}
                  initialAccentColor={project.accentColor}
                />
                <button
                  className="btn-submit"
                  type="button"
                  onClick={() => {
                    handleCopyColors();
                    setShowTableColorModal(false);
                  }}
                >
                  Guardar colores base
                </button>
              </div>
            </div>
          )}

          {/* Modal para StatusColorForm */}
          {showColorModal && (
            <div className="modal-overlay" onClick={() => setShowColorModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button
                  className="close-button"
                  onClick={() => setShowColorModal(false)}
                  aria-label="Cerrar modal"
                >
                  &times;
                </button>
                <StatusColorForm
                  initialColors={project.statusColors || {}}
                  onSave={(newColors) => {
                    handleStatusColorsChange(newColors);
                    setShowColorModal(false);
                  }}
                  onClose={() => setShowColorModal(false)}
                />
              </div>
            </div>
          )}

          {showDeleteModal && (
            <div className="media-modal-overlay" onClick={closeDeleteModal}>
              <div className="media-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-content-h3">¿Seguro que deseas eliminar este archivo?</h3>
                <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                  <button className="btn-submit" onClick={confirmDelete}>Aceptar</button>
                  <button className="btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
                </div>
              </div>
            </div>
          )}
          {showImportModal && importResult && (
            <div className="media-modal-overlay" onClick={() => setShowImportModal(false)}>
              <div className="media-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-content-h3">Resultado Importación</h3>
                <div style={{ padding: "8px 16px" }}>
                  <p>Unidades importadas: <strong>{importResult.imported ?? 0}</strong></p>
                  <p>Unidades actualizadas: <strong>{importResult.updated ?? 0}</strong></p>
                  <p>Errores: <strong>{(importResult.errors && importResult.errors.length) ?? 0}</strong></p>
                </div>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px", justifyContent: "center" }}>
                  <button className="btn-submit" onClick={() => setShowImportModal(false)}>Cerrar</button>
                  <button
                    className="btn-action"
                    onClick={() => {
                      setShowImportErrors(true);
                    }}
                    disabled={!(importResult.errors && importResult.errors.length)}
                  >
                    Ver errores
                  </button>
                </div>
              </div>
            </div>
          )}
                    
          {showImportErrors && importResult && importResult.errors && (
            <div className="media-modal-overlay" onClick={() => setShowImportErrors(false)}>
              <div className="media-modal wide" onClick={e => e.stopPropagation()}>
                <h3 className="modal-content-h3">Detalle de Errores</h3>
                <div style={{ maxHeight: "60vh", overflow: "auto", padding: "8px 16px" }}>
                  <table className={styles.table} style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Unidad</th>
                        <th>Mensaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((err, i) => (
                        <tr key={i}>
                          <td>{err.row ?? ''}</td>
                          <td>{err.unit ?? ''}</td>
                          <td>{err.error ?? err.message ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
               </div>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px", justifyContent: "center" }}>
                  <button className="btn-submit" onClick={() => setShowImportErrors(false)}>Cerrar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectForm;
